import { useEffect, useMemo } from 'react';

import { Amount } from '@/components/common/Amount';
import { Button } from '@/components/common/Button';
import { Menu } from '@/components/common/Menu';
import { SearchInput } from '@/components/common/Input';
import { LoadingMessage, EmptyState } from '@/components/common/Spinner';
import { Pill, type PillTone } from '@/components/common/Pill';
import {
  SvgAdd,
  SvgAlert,
  SvgCalendar3,
  SvgCheck,
  SvgDotsVertical,
  SvgFlag,
} from '@/components/icons';
import { useAccounts } from '@/store/app';
import { useSchedulesStore } from '@/store/reports';
import { formatDate } from '@/utils/months';

const TONES: Record<string, PillTone> = {
  paid: 'paid',
  due: 'due',
  missed: 'missed',
  upcoming: 'upcoming',
  scheduled: 'neutral',
};

const STATUS_LABELS: Record<string, string> = {
  paid: '已支付',
  due: '到期',
  missed: '已逾期',
  upcoming: '即将发生',
  scheduled: '已安排',
};

const COLUMNS = 'minmax(150px,1.1fr) minmax(130px,1fr) minmax(130px,1fr) 110px 120px 110px 70px 40px';

/** Port of `components/schedules/index.tsx` + `ScheduleTable.tsx`. */
export function SchedulesPage() {
  const schedules = useSchedulesStore(state => state.schedules);
  const loading = useSchedulesStore(state => state.loading);
  const filter = useSchedulesStore(state => state.filter);
  const setFilter = useSchedulesStore(state => state.setFilter);
  const accounts = useAccounts();

  useEffect(() => {
    void useSchedulesStore.getState().load();
  }, []);

  const accountName = useMemo(
    () => new Map(accounts.map(account => [account.id, account.name])),
    [accounts],
  );

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return schedules;
    return schedules.filter(schedule =>
      [schedule.name, schedule.payee_name ?? '', accountName.get(schedule.account ?? '') ?? '']
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [schedules, filter, accountName]);

  if (loading && schedules.length === 0) {
    return <LoadingMessage message="正在加载周期账单…" />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="schedules-page">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-5 pt-1">
        <h1 className="text-[25px] font-medium text-page-text">周期账单</h1>
        <SearchInput
          className="w-[260px]"
          placeholder="筛选周期账单…"
          aria-label="筛选周期账单"
          value={filter}
          onChange={event => setFilter(event.target.value)}
        />
      </div>

      <div className="scrollable m-5 min-h-0 flex-1 overflow-auto rounded-md border border-table-border bg-table-background">
        <div style={{ minWidth: 860 }}>
          <div
            className="sticky top-0 z-10 grid border-b border-table-border bg-table-header-background text-sm text-table-header-text"
            style={{ gridTemplateColumns: COLUMNS }}
          >
            <div className="px-3 py-2">名称</div>
            <div className="px-3 py-2">收款人</div>
            <div className="px-3 py-2">账户</div>
            <div className="px-3 py-2">下次日期</div>
            <div className="px-3 py-2">状态</div>
            <div className="px-3 py-2 text-right">金额</div>
            <div className="px-3 py-2">周期性</div>
            <div />
          </div>

          {rows.map(schedule => (
            <div
              key={schedule.id}
              className="grid items-center border-b border-table-border text-sm hover:bg-table-row-background-hover"
              style={{ gridTemplateColumns: COLUMNS }}
              data-testid={`schedule-row-${schedule.name}`}
            >
              <div className="truncate px-3 py-3 text-table-text">
                {schedule.name}
              </div>
              <div
                className={
                  schedule.payee_name
                    ? 'truncate px-3 py-3 text-table-text'
                    : 'truncate px-3 py-3 text-table-text-light'
                }
              >
                {schedule.payee_name ?? '无'}
              </div>
              <div
                className={
                  schedule.account
                    ? 'truncate px-3 py-3 text-table-text'
                    : 'truncate px-3 py-3 text-table-text-light'
                }
              >
                {accountName.get(schedule.account ?? '') ?? '无'}
              </div>
              <div className="tnum px-3 py-3 text-table-text">
                {formatDate(schedule.next_date)}
              </div>
              <div className="px-3 py-3">
                <Pill
                  tone={TONES[schedule.status] ?? 'neutral'}
                  icon={
                    schedule.status === 'paid' ? (
                      <SvgCheck width={11} height={11} />
                    ) : schedule.status === 'due' ? (
                      <SvgAlert width={11} height={11} />
                    ) : schedule.status === 'missed' ? (
                      <SvgFlag width={11} height={11} />
                    ) : schedule.status === 'scheduled' ? (
                      <SvgCalendar3 width={11} height={11} />
                    ) : null
                  }
                >
                  {STATUS_LABELS[schedule.status] ?? schedule.status}
                </Pill>
              </div>
              <div className="px-3 py-3 text-right">
                <Amount value={Math.abs(schedule.amount)} />
              </div>
              <div className="px-3 py-3 text-table-text">
                {schedule.approx_amount ? '~' : <SvgCheck width={13} height={13} />}
              </div>
              <div className="px-1 py-3">
                <Menu
                  width={170}
                  button={
                    <span className="inline-flex cursor-pointer rounded-sm p-1 text-table-text-light hover:bg-table-row-background-hover">
                      <SvgDotsVertical width={14} height={14} />
                    </span>
                  }
                  items={[{ label: '暂停该周期账单', disabled: true }]}
                />
              </div>
            </div>
          ))}

          {rows.length === 0 && (
            <EmptyState
              title="没有匹配的周期账单"
              hint="清空「筛选周期账单」输入框即可看回全部账单。"
            />
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between px-5 pb-4">
        <span className="text-sm text-page-text-subdued">
          共 {schedules.length} 条周期账单
        </span>
        <Button
          size="sm"
          variant="primary"
          icon={<SvgAdd width={12} height={12} />}
          disabled
          title="周期账单编辑器不在本复刻范围内"
        >
          新增周期账单
        </Button>
      </div>
    </div>
  );
}
